import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Stack,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Checkbox,
    IconButton,
    Alert,
    Skeleton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Avatar,
    Tooltip,
} from '@mui/material';
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    useDroppable,
    DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BuildIcon from '@mui/icons-material/Build';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InboxIcon from '@mui/icons-material/Inbox';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TableRowsIcon from '@mui/icons-material/TableRows';
import PrintIcon from '@mui/icons-material/Print';
import apiClient from '../api';
import { useAuth } from '../context/AuthContext';
import {
    SERVICE_LANES,
    SERVICE_STATUSES,
    JOB_STATUSES,
    canEditServiceTickets,
    laneTitle,
    truckOf,
    unitLabel,
    openJobs,
    shortDate,
    ageLabel,
    jobStatus,
    roleLabel,
    whenLabel,
} from '../utils/serviceTickets';
import { printShopWorkList } from '../utils/serviceTicketPrint';

const LANE_ICONS = {
    new: <InboxIcon />,
    in_shop: <BuildIcon />,
    waiting_parts: <HourglassEmptyIcon />,
    thermo_king: <AcUnitIcon />,
    ready: <CheckCircleIcon />,
};

function blankJob() {
    return { text: '', status: 'open', note: '' };
}

function ticketForm(ticket) {
    return {
        truckId: truckOf(ticket)._id || (typeof ticket?.truck === 'string' ? ticket.truck : ''),
        status: ticket?.status || 'new',
        down: Boolean(ticket?.down),
        notes: ticket?.notes || '',
        items: (ticket?.items || []).map((item) => ({
            _id: item._id,
            text: item.text || '',
            status: jobStatus(item),
            note: item.note || '',
        })),
    };
}

function BoardCardSkeletons() {
    return (
        <Stack spacing={1}>
            {[0, 1].map((i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                    <Skeleton variant="text" width="55%" height={24} />
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Paper>
            ))}
        </Stack>
    );
}

function TicketCard({ ticket, onOpen }) {
    const jobs = openJobs(ticket);
    const make = [truckOf(ticket).make, truckOf(ticket).type].filter(Boolean).join(' · ');
    return (
        <Card
            onClick={onOpen}
            sx={{
                mb: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: ticket.unread ? '#90caf9' : (ticket.down ? '#ef9a9a' : '#e7e5e4'),
                borderLeft: '4px solid',
                borderLeftColor: ticket.down ? '#c62828' : (ticket.unread ? '#1976d2' : '#E94F37'),
                boxShadow: '0 1px 2px rgba(28, 25, 23, 0.06)',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: 3 },
            }}
        >
            <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.2 }} noWrap>
                        {unitLabel(ticket)}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                        {ticket.unread && <Chip label="New" size="small" color="info" sx={{ height: 20, fontWeight: 700 }} />}
                        {ticket.down && <Chip label="Down" size="small" color="error" sx={{ height: 20, fontWeight: 700 }} />}
                    </Stack>
                </Stack>
                {make && (
                    <Typography variant="caption" color="text.secondary" noWrap>{make}</Typography>
                )}
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }} noWrap>
                    {jobs.length === 0 ? 'No open jobs' : jobs[0].text}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {jobs.length} open · {ageLabel(ticket)}
                </Typography>
            </CardContent>
        </Card>
    );
}

function SortableCard({ ticket, onOpen }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ticket._id });
    return (
        <Box
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
            {...attributes}
            {...listeners}
        >
            <TicketCard ticket={ticket} onOpen={onOpen} />
        </Box>
    );
}

function LockedCard({ ticket, onOpen }) {
    const [hint, setHint] = useState(false);
    const start = useRef(null);
    const moved = useRef(false);
    const hideTimer = useRef(null);

    useEffect(() => () => clearTimeout(hideTimer.current), []);

    const showHint = () => {
        setHint(true);
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setHint(false), 2200);
    };

    return (
        <Tooltip
            title="Only a service admin can move this ticket"
            open={hint}
            placement="top"
            arrow
            disableHoverListener
            disableFocusListener
            disableTouchListener
        >
            <Box
                onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    start.current = { x: event.clientX, y: event.clientY };
                    moved.current = false;
                    const onMove = (moveEvent) => {
                        if (!start.current || moved.current) return;
                        const dx = moveEvent.clientX - start.current.x;
                        const dy = moveEvent.clientY - start.current.y;
                        if ((dx * dx) + (dy * dy) < 36) return;
                        moved.current = true;
                        showHint();
                    };
                    const onUp = () => {
                        window.removeEventListener('pointermove', onMove);
                        window.removeEventListener('pointerup', onUp);
                        window.removeEventListener('pointercancel', onUp);
                        start.current = null;
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                    window.addEventListener('pointercancel', onUp);
                }}
                onClickCapture={(event) => {
                    if (!moved.current) return;
                    moved.current = false;
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                <TicketCard ticket={ticket} onOpen={onOpen} />
            </Box>
        </Tooltip>
    );
}

function truckIdOf(truck) {
    return String(truck?._id || truck?.id || '');
}

function Lane({ column, tickets, ready, canEdit, onOpen, onAdd }) {
    const { setNodeRef, isOver } = useDroppable({ id: column.id });
    const ids = tickets.map((ticket) => ticket._id);
    return (
        <Paper
            sx={{
                minWidth: { xs: 220, sm: 240 },
                maxWidth: { xs: 220, sm: 260 },
                flex: '1 0 auto',
                borderTop: '2px solid',
                borderTopColor: column.color,
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 2px rgba(28, 25, 23, 0.05)',
                backgroundColor: isOver ? 'rgba(233, 79, 55, 0.06)' : 'background.paper',
            }}
        >
            <Box sx={{ px: 1.25, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', borderTopLeftRadius: 6, borderTopRightRadius: 6 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                    <Avatar sx={{ bgcolor: column.color, width: 26, height: 26, '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                        {LANE_ICONS[column.id]}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{column.title}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    {ready ? (
                        <Chip label={`${tickets.length}`} size="small" sx={{ height: 22 }} />
                    ) : (
                        <Skeleton variant="rounded" width={28} height={22} />
                    )}
                    {onAdd && (
                        <Button size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ minWidth: 0, py: 0 }}>
                            Add
                        </Button>
                    )}
                </Stack>
            </Box>
            <Box ref={setNodeRef} sx={{ p: 1, minHeight: 280, maxHeight: 'min(58vh, 520px)', overflowY: 'auto' }}>
                {!ready ? <BoardCardSkeletons /> : tickets.length === 0 ? (
                    <Box sx={{ p: 2, border: '2px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {onAdd ? (
                            <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add ticket</Button>
                        ) : (
                            <Typography variant="caption" color="text.secondary">{canEdit ? 'Drop here' : 'Empty'}</Typography>
                        )}
                    </Box>
                ) : canEdit ? (
                    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                        {tickets.map((ticket) => (
                            <SortableCard key={ticket._id} ticket={ticket} onOpen={() => onOpen(ticket)} />
                        ))}
                    </SortableContext>
                ) : tickets.map((ticket) => (
                    <LockedCard key={ticket._id} ticket={ticket} onOpen={() => onOpen(ticket)} />
                ))}
            </Box>
        </Paper>
    );
}

function ServicePage() {
    const { user } = useAuth();
    const canEdit = canEditServiceTickets(user?.role);
    const [searchParams, setSearchParams] = useSearchParams();
    const [tickets, setTickets] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');
    const [view, setView] = useState('board');
    const [showClosed, setShowClosed] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(ticketForm(null));
    const [saving, setSaving] = useState(false);
    const skipClick = useRef(false);
    const skipRead = useRef(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const load = async () => {
        setError('');
        try {
            const [ticketRes, truckRes] = await Promise.all([
                apiClient.get('/service-tickets'),
                apiClient.get('/trucks'),
            ]);
            const next = ticketRes.data?.data || [];
            setTickets(next);
            const fleet = Array.isArray(truckRes.data) ? truckRes.data : (truckRes.data?.data || []);
            setTrucks(fleet);
            return next;
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load tickets');
            return [];
        } finally {
            setReady(true);
        }
    };

    useEffect(() => { load(); }, []);

    function applyTicket(saved) {
        if (!saved?._id) return;
        setTickets((prev) => prev.map((ticket) => (ticket._id === saved._id ? saved : ticket)));
        setEditing((current) => (current && current._id === saved._id ? saved : current));
    }

    async function markRead(ticket) {
        if (!ticket?._id || !ticket.unread || skipRead.current) return;
        try {
            const res = await apiClient.post(`/service-tickets/${ticket._id}/read`);
            if (skipRead.current) return;
            applyTicket(res.data?.data);
        } catch (_) {
            // Opening still works if the read mark fails.
        }
    }

    useEffect(() => {
        if (!ready) return;
        const id = searchParams.get('ticket');
        const wantNew = searchParams.get('new') === '1';
        if (!id && !wantNew) return;
        if (id) {
            const found = tickets.find((ticket) => ticket._id === id);
            if (found) {
                skipRead.current = false;
                setEditing(found);
                setForm(ticketForm(found));
                setDialogOpen(true);
                setError('');
                if (found.unread) {
                    apiClient.post(`/service-tickets/${found._id}/read`).then((res) => {
                        if (skipRead.current) return;
                        const saved = res.data?.data;
                        if (!saved?._id) return;
                        setTickets((prev) => prev.map((ticket) => (ticket._id === saved._id ? saved : ticket)));
                        setEditing((current) => (current && current._id === saved._id ? saved : current));
                    }).catch(() => {});
                }
            }
        } else if (wantNew && canEdit) {
            setEditing(null);
            setForm({ ...ticketForm(null), items: [blankJob()] });
            setDialogOpen(true);
            setError('');
        }
        setSearchParams({}, { replace: true });
    }, [ready, tickets, searchParams, setSearchParams, canEdit]);

    const openTickets = useMemo(
        () => tickets.filter((ticket) => ticket.status !== 'closed'),
        [tickets]
    );

    const tableRows = useMemo(() => {
        const rows = showClosed ? tickets : openTickets;
        return [...rows].sort((a, b) => String(unitLabel(a)).localeCompare(String(unitLabel(b)), undefined, { numeric: true }));
    }, [showClosed, tickets, openTickets]);

    function openTicket(ticket) {
        if (skipClick.current) return;
        skipRead.current = false;
        setEditing(ticket);
        setForm(ticket ? ticketForm(ticket) : { ...ticketForm(null), items: [blankJob()] });
        setDialogOpen(true);
        setError('');
        markRead(ticket);
    }

    async function markUnread() {
        if (!editing?._id) return;
        skipRead.current = true;
        setSaving(true);
        setError('');
        try {
            const res = await apiClient.post(`/service-tickets/${editing._id}/unread`);
            const saved = res.data?.data;
            setTickets((prev) => prev.map((ticket) => (ticket._id === saved._id ? saved : ticket)));
            setDialogOpen(false);
        } catch (err) {
            skipRead.current = false;
            setError(err.response?.data?.message || 'Could not mark unread');
        } finally {
            setSaving(false);
        }
    }

    function openNew() {
        setEditing(null);
        setForm({ ...ticketForm(null), items: [blankJob()] });
        setDialogOpen(true);
        setError('');
    }

    const taken = new Set(openTickets.map((ticket) => String(truckOf(ticket)._id || truckOf(ticket).id || ticket.truck)));
    const truckChoices = [...trucks]
        .filter((truck) => truckIdOf(truck) && !taken.has(truckIdOf(truck)))
        .sort((a, b) => String(a.unitNumber || '').localeCompare(String(b.unitNumber || ''), undefined, { numeric: true }));

    async function save(nextStatus) {
        const payload = {
            status: nextStatus || form.status,
            down: form.down,
            notes: form.notes,
            items: form.items,
        };
        setSaving(true);
        setError('');
        try {
            if (editing) {
                const res = await apiClient.patch(`/service-tickets/${editing._id}`, payload);
                const saved = res.data?.data;
                setTickets((prev) => prev.map((ticket) => (ticket._id === saved._id ? saved : ticket)));
            } else {
                const res = await apiClient.post('/service-tickets', { ...payload, truckId: form.truckId });
                const saved = res.data?.data;
                setTickets((prev) => [saved, ...prev]);
            }
            setDialogOpen(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not save the ticket');
        } finally {
            setSaving(false);
        }
    }

    async function removeTicket() {
        if (!editing) return;
        setSaving(true);
        try {
            await apiClient.delete(`/service-tickets/${editing._id}`);
            setTickets((prev) => prev.filter((ticket) => ticket._id !== editing._id));
            setDialogOpen(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not delete the ticket');
        } finally {
            setSaving(false);
        }
    }

    async function moveTicket(ticketId, status) {
        const current = tickets.find((ticket) => ticket._id === ticketId);
        if (!current || current.status === status) return;
        setTickets((prev) => prev.map((ticket) => (ticket._id === ticketId ? { ...ticket, status } : ticket)));
        try {
            const res = await apiClient.patch(`/service-tickets/${ticketId}`, { status });
            const saved = res.data?.data;
            setTickets((prev) => prev.map((ticket) => (ticket._id === saved._id ? saved : ticket)));
        } catch (err) {
            setTickets((prev) => prev.map((ticket) => (ticket._id === ticketId ? current : ticket)));
            setError(err.response?.data?.message || 'Could not move the ticket');
        }
    }

    function onDragEnd(event) {
        setActiveId(null);
        skipClick.current = true;
        setTimeout(() => { skipClick.current = false; }, 80);
        if (!event.over) return;
        const overId = String(event.over.id);
        const lane = SERVICE_LANES.find((column) => column.id === overId);
        const status = lane ? lane.id : tickets.find((ticket) => ticket._id === overId)?.status;
        if (status) moveTicket(String(event.active.id), status);
    }

    const activeTicket = tickets.find((ticket) => ticket._id === activeId);
    const dialogTitle = editing ? unitLabel(editing) : 'Add ticket';

    const board = (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5 }}>
            {SERVICE_LANES.map((column) => (
                <Lane
                    key={column.id}
                    column={column}
                    ready={ready}
                    canEdit={canEdit}
                    tickets={openTickets.filter((ticket) => ticket.status === column.id)}
                    onOpen={openTicket}
                    onAdd={canEdit && column.id === 'new' ? openNew : null}
                />
            ))}
        </Box>
    );

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} spacing={1}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>Tickets</Typography>
                    <Typography variant="body2" color="text.secondary">{canEdit ? 'Drag a truck to move it' : 'Open a ticket to view it. Only a service admin can move one.'}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={view}
                        onChange={(_, next) => { if (next) setView(next); }}
                    >
                        <ToggleButton value="board" aria-label="Board"><ViewColumnIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="table" aria-label="Table"><TableRowsIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                    {canEdit && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add ticket</Button>
                    )}
                </Stack>
            </Stack>
            {error && !dialogOpen && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

            {view === 'board' ? (
                <DndContext
                    sensors={canEdit ? sensors : []}
                    collisionDetection={closestCenter}
                    onDragStart={(event) => { if (canEdit) setActiveId(String(event.active.id)); }}
                    onDragEnd={canEdit ? onDragEnd : () => setActiveId(null)}
                >
                    {board}
                    <DragOverlay>
                        {activeTicket ? <TicketCard ticket={activeTicket} onOpen={() => {}} /> : null}
                    </DragOverlay>
                </DndContext>
            ) : (
                <Paper>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1 }}>
                        <FormControlLabel
                            control={<Checkbox checked={showClosed} onChange={(event) => setShowClosed(event.target.checked)} />}
                            label="Show closed"
                        />
                        <Button startIcon={<PrintIcon />} onClick={() => printShopWorkList(tableRows)}>
                            Print work list
                        </Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Unit</TableCell>
                                <TableCell>Make</TableCell>
                                <TableCell>Down</TableCell>
                                <TableCell>Lane</TableCell>
                                <TableCell align="right">Open jobs</TableCell>
                                <TableCell>First open job</TableCell>
                                <TableCell>Opened</TableCell>
                                <TableCell>Updated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!ready ? (
                                <TableRow>
                                    <TableCell colSpan={8}><Skeleton height={32} /></TableCell>
                                </TableRow>
                            ) : tableRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8}>No tickets</TableCell>
                                </TableRow>
                            ) : tableRows.map((ticket) => {
                                const jobs = openJobs(ticket);
                                return (
                                    <TableRow key={ticket._id} hover sx={{ cursor: 'pointer' }} onClick={() => openTicket(ticket)}>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                <span>{unitLabel(ticket)}</span>
                                                {ticket.unread && <Chip label="New" size="small" color="info" sx={{ height: 20, fontWeight: 700 }} />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{truckOf(ticket).make || ''}</TableCell>
                                        <TableCell>{ticket.down ? 'Yes' : ''}</TableCell>
                                        <TableCell>{laneTitle(ticket.status)}</TableCell>
                                        <TableCell align="right">{jobs.length}</TableCell>
                                        <TableCell>{jobs[0]?.text || ''}</TableCell>
                                        <TableCell>{shortDate(ticket.createdAt)}</TableCell>
                                        <TableCell>{shortDate(ticket.updatedAt)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    {error && dialogOpen && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                        {editing ? (
                            <Typography variant="body2" color="text.secondary">
                                {[truckOf(editing).year, truckOf(editing).make, truckOf(editing).model, truckOf(editing).type].filter(Boolean).join(' ')}
                            </Typography>
                        ) : (
                            <Box>
                                <FormControl fullWidth disabled={!canEdit}>
                                    <InputLabel>Truck</InputLabel>
                                    <Select
                                        label="Truck"
                                        value={form.truckId}
                                        onChange={(event) => setForm({ ...form, truckId: event.target.value })}
                                    >
                                        {truckChoices.length === 0 && (
                                            <MenuItem value="" disabled>No trucks available</MenuItem>
                                        )}
                                        {truckChoices.map((truck) => (
                                            <MenuItem key={truckIdOf(truck)} value={truckIdOf(truck)}>
                                                {truck.unitNumber}{truck.make ? ` ${truck.make}` : ''}{truck.type ? ` · ${truck.type}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {truckChoices.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        No trucks are available to add. Each truck can have one open ticket.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <Stack direction="row" spacing={1}>
                            <FormControl fullWidth disabled={!canEdit}>
                                <InputLabel>Lane</InputLabel>
                                <Select
                                    label="Lane"
                                    value={form.status}
                                    onChange={(event) => setForm({ ...form, status: event.target.value })}
                                >
                                    {(editing ? SERVICE_STATUSES : SERVICE_LANES).map((lane) => (
                                        <MenuItem key={lane.id} value={lane.id}>{lane.title}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={form.down}
                                        disabled={!canEdit}
                                        onChange={(event) => setForm({ ...form, down: event.target.checked })}
                                    />
                                )}
                                label="Down"
                            />
                        </Stack>
                        <Typography variant="subtitle2">Jobs</Typography>
                        {form.items.map((item, index) => (
                            <Paper key={item._id || index} variant="outlined" sx={{ p: 1.25 }}>
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder={index === 0 ? 'Check engine light on' : 'Needs new wipers'}
                                            value={item.text}
                                            disabled={!canEdit}
                                            onChange={(event) => {
                                                const items = form.items.slice();
                                                items[index] = { ...item, text: event.target.value };
                                                setForm({ ...form, items });
                                            }}
                                        />
                                        {canEdit && (
                                            <IconButton
                                                aria-label="Remove job"
                                                onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <FormControl size="small" sx={{ minWidth: 180 }} disabled={!canEdit}>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                label="Status"
                                                value={item.status || 'open'}
                                                onChange={(event) => {
                                                    const items = form.items.slice();
                                                    items[index] = { ...item, status: event.target.value };
                                                    setForm({ ...form, items });
                                                }}
                                            >
                                                {JOB_STATUSES.map((status) => (
                                                    <MenuItem key={status.id} value={status.id}>{status.title}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Note for the driver"
                                            value={item.note || ''}
                                            disabled={!canEdit}
                                            onChange={(event) => {
                                                const items = form.items.slice();
                                                items[index] = { ...item, note: event.target.value };
                                                setForm({ ...form, items });
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                            </Paper>
                        ))}
                        {canEdit && (
                            <Button startIcon={<AddIcon />} onClick={() => setForm({ ...form, items: [...form.items, blankJob()] })}>
                                Add job
                            </Button>
                        )}
                        <TextField
                            label="Notes"
                            multiline
                            minRows={2}
                            value={form.notes}
                            disabled={!canEdit}
                            onChange={(event) => setForm({ ...form, notes: event.target.value })}
                        />
                        {editing && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>History</Typography>
                                {(editing.activity || []).length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No history yet.</Typography>
                                ) : (
                                    <Stack spacing={0.5}>
                                        {(editing.activity || []).map((entry, index) => (
                                            <Typography key={`${entry.at}-${index}`} variant="body2">
                                                {whenLabel(entry.at)} · {entry.name}{roleLabel(entry.role) ? ` (${roleLabel(entry.role)})` : ''} — {entry.detail}
                                            </Typography>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {canEdit && editing && (
                        <Button color="error" disabled={saving} onClick={removeTicket}>Delete</Button>
                    )}
                    {editing && !editing.unread && (
                        <Button disabled={saving} onClick={markUnread}>Mark unread</Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => setDialogOpen(false)}>{canEdit ? 'Cancel' : 'Close'}</Button>
                    {canEdit && editing && form.status !== 'closed' && (
                        <Button disabled={saving} onClick={() => save('closed')}>Close ticket</Button>
                    )}
                    {canEdit && (
                        <Button variant="contained" disabled={saving || (!editing && !form.truckId)} onClick={() => save()}>
                            {editing ? 'Save' : 'Create ticket'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ServicePage;
